import React, { useEffect, useMemo, useState } from "react";
import styles from "./Login.module.css";
import { useNavigate, Link } from "react-router-dom";
import { useDispatch, useSelector } from "react-redux";
import FormData from "form-data";
import { signIn } from "./../../redux/actions/action-creator";
import { ToastContainer, toast } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";
import { countriesData } from "./../../constant/countries";
import { clearGuestSession, performGuestLogin } from "../../utils/guestLogin";
import { selectConfigurationData } from "../../redux/selectors/configurationSelectors";
import { getScreenConfig } from "../../utils/uiConfig";
import { getDefaultAuthPhoneCountry } from "../../utils/authPhoneCountry";

const initialState = {
  customercode: 1,
  phone: "",
  password: "",
};

const isSuccessResponse = (response) =>
  response?.result === true ||
  response?.result === 1 ||
  response?.result === "1";

const Login = () => {
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const configurationData = useSelector(selectConfigurationData);
  const { user_location, country_id } = useSelector((reduxState) => reduxState.auth || {});
  const authUi = getScreenConfig(configurationData, "login");
  const defaultPhoneCountry = useMemo(
    () =>
      getDefaultAuthPhoneCountry({
        countryId: country_id,
        userLocation: user_location,
      }),
    [country_id, user_location]
  );

  const [state, setState] = useState(initialState);
  const [selectedCountry, setSelectedCountry] = useState(defaultPhoneCountry);
  const [loading, setLoading] = useState(false);
  const [loadingGuest, setLoadingGuest] = useState(false);
  const [showPassword, setShowPassword] = useState(false);

  const { customercode, phone, password } = state;
  const authStyle = {
    "--auth-bg": authUi.backgroundColor || "#ffffff",
    "--auth-input-bg": authUi.inputBackgroundColor || "#ffffff",
    "--auth-button-bg": authUi.buttonColor || "#42489b",
    "--auth-button-text": authUi.buttonTextColor || "#ffffff",
    "--auth-link": authUi.linkColor || "#24ca74",
  };

  useEffect(() => {
    setSelectedCountry(defaultPhoneCountry);
  }, [defaultPhoneCountry]);

  // Input change handler
  const handleChange = (e) => {
    const nextValue =
      e.target.name === "phone" ? e.target.value.replace(/\D/g, "") : e.target.value;

    setState({
      ...state,
      [e.target.name]: nextValue,
    });
  };

  const handleSubmit = (e) => {
    e.preventDefault();

    if (
      phone.length < selectedCountry.minNumPhone ||
      phone.length > selectedCountry.maxNumPhone
    ) {
      toast.warn(
        `Phone number must be between ${selectedCountry.minNumPhone} and ${selectedCountry.maxNumPhone} digits for ${selectedCountry.name}.`
      );
      return;
    }

    if (customercode && phone && password) {
      setLoading(true);

      let data = new FormData();

      data.append(
        "phone",
        `${selectedCountry.phonecode}${phone}`
      );

      data.append("password", password);
      data.append("customer_code", customercode);

      dispatch(signIn(data))
        .then((response) => {
          if (isSuccessResponse(response)) {
            localStorage.setItem(
              "access_token",
              response.data?.access_token
            );

            localStorage.setItem(
              "userRole",
              response.data?.role_type
            );

            toast.success("Login Success!");

            navigate("/home");
          } else {
            toast.warn(response?.message || "Login Failed!");
          }

          setLoading(false);
        })
        .catch((err) => {
          toast.error(err?.message || "Login Failed!");

          setLoading(false);
        });
    } else {
      toast.warn("Please fill all fields!");
    }
  };

  const handleBack = () => {
    if (window.history.length > 1) {
      navigate(-1);
      return;
    }

    navigate("/home");
  };

  const handleGuestLogin = async () => {
    if (loadingGuest) return;

    setLoadingGuest(true);

    try {
      await performGuestLogin(dispatch);
      toast.success("Guest login successful. You can browse the site now.");
      navigate("/home");
    } catch (error) {
      clearGuestSession();
      toast.error(error?.message || "Guest login failed.");
    } finally {
      setLoadingGuest(false);
    }
  };

  return (
    <>
      <ToastContainer />

      <div className={styles["login-page"]} style={authStyle}>
        <div className={styles["mobile-auth-top"]}>
          <button
            type="button"
            className={styles["mobile-back-button"]}
            onClick={handleBack}
            aria-label="Go back"
          >
            <i className="ph ph-arrow-left" />
          </button>
          <button
            type="button"
            className={styles["mobile-skip-button"]}
            onClick={handleGuestLogin}
            disabled={loadingGuest}
          >
            {loadingGuest ? "Please wait..." : "Login Skip"}
          </button>
        </div>

        <div className={styles["login-card"]}>

          {/* LEFT BANNER */}
          <div className={styles["login-banner"]}>
            
            <div>
              <h2>
                Fast, Reliable <br />
                Service Straight <br />
                to Your Door.
              </h2>

              <p>We Deliver Happiness.</p>
            </div>

            <div className={styles["login-banner-div"]}>
              <img
                src={`${process.env.PUBLIC_URL}/assets/images/auth/login-image.png`}
                alt="Grocery"
                className={styles["banner-img"]}
              />
            </div>
          </div>

          {/* RIGHT LOGIN */}
          <form className={`${styles["login-form"]} text-center`} onSubmit={handleSubmit} noValidate>

            <img
              src={`${process.env.PUBLIC_URL}/assets/images/logo/rghtnow-blue-logo.png`}
              alt="logo"
              className={styles["logo"]}
            />

            <h3>Welcome Back</h3>

            <p className={styles["sub-text"]}>
              Please login to your account
            </p>

            <p className={styles["mobile-copy"]}>
              Get your groceries with <br />
              <span>right now</span>
            </p>

            {/* COUNTRY + PHONE */}
            <div className={styles["country-phone-container"]}>

              <div className={styles["country-select-wrapper"]}>
                <span className={styles["country-compact"]}>
                  {selectedCountry.flagEmoji} {selectedCountry.phonecode}
                </span>
                <select
                  name="country"
                  value={selectedCountry.id}
                  onChange={(e) => {
                    const country = countriesData.find(
                      (c) =>
                        c.id === parseInt(e.target.value)
                    );

                    setSelectedCountry(country || defaultPhoneCountry);
                  }}
                  className={styles["country-select"]}
                >
                  {countriesData.map((country) => (
                    <option
                      key={country.id}
                      value={country.id}
                    >
                      {country.flagEmoji}{" "}
                      {country.phonecode}{" "}
                      {country.name}
                    </option>
                  ))}
                </select>
              </div>

              <div className={styles["phone-input-wrapper"]}>
                <input
                  type="text"
                  name="phone"
                  value={state.phone}
                  onChange={handleChange}
                  placeholder="Enter phone number"
                  className={`${styles["login-input"]} ${styles["phone-input"]}`}
                  maxLength={
                    selectedCountry.maxNumPhone
                  }
                />
              </div>
            </div>

            {/* PASSWORD */}
            <div className={styles["password-field"]}>
              <i className="ph ph-lock-key" />
              <input
                name="password"
                type={showPassword ? "text" : "password"}
                value={state.password}
                onChange={handleChange}
                placeholder="Password"
                className={styles["login-input"]}
              />
              <button
                type="button"
                className={styles["password-toggle"]}
                onClick={() => setShowPassword((visible) => !visible)}
                aria-label={showPassword ? "Hide password" : "Show password"}
              >
                <i className={showPassword ? "ph ph-eye-slash" : "ph ph-eye"} />
              </button>
            </div>

            {/* OPTIONS */}
            <div className={styles["form-options"]}>

              <label className={styles["remember"]}>
                <input
                  type="checkbox"
                  name="customercode"
                  checked={
                    state.customercode === 1
                  }
                  onChange={(e) =>
                    setState({
                      ...state,
                      customercode: e.target.checked
                        ? 1
                        : 0,
                    })
                  }
                />

                Remember me
              </label>

              <a
                href="/"
                className={styles["forgot"]}
              >
                Forgot password?
              </a>
            </div>

            {/* LOGIN BUTTON */}
            <button
              type="submit"
              className={styles["btn-login"]}
              disabled={loading}
            >
              {loading ? "Processing..." : (
                <>
                  Login <i className="ph ph-arrow-right" />
                </>
              )}
            </button>

            {/* SIGNUP */}
            <p className={styles["signup-text"]}>
              Don’t have an account?
              <Link to="/signUp">
                <span> Signup</span>
              </Link>
            </p>

          </form>
        </div>

        <img
          src={
            authUi.footerImage ||
            `${process.env.PUBLIC_URL}/assets/images/auth/generated/auth-fruit-footer-crop.png`
          }
          alt=""
          className={styles["mobile-fruits"]}
        />
      </div>
    </>
  );
};

export default Login;
